import React, { useState } from 'react';

import moment from 'moment';
import styled from 'styled-components';
import {
  Button,
  Input,
  Table,
  Space,
  Switch,
  Modal,
  DatePicker,
  Tag,
  Tooltip,
  Divider,
  Row,
  Col,
} from 'antd';
import { toast } from 'react-hot-toast';
import {
  CheckCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
  BellOutlined,
  AlertOutlined,
  DesktopOutlined,
  UserOutlined,
  TeamOutlined,
  GlobalOutlined,
  ClockCircleOutlined,
  PushpinOutlined,
  EditOutlined,
  PlusOutlined,
  StopOutlined,
} from '@ant-design/icons';

import SiteAdmin from '../components/SiteAdmin';
import Page from 'components/Page';
import withAdminOnly from '../components/AdminOnly';
import { ProviderAPI, useGetApi, useMutateApi } from 'providerSite/api';
import RichTextField from 'components/input/RichTextField';


const Container = styled.div`
  width: 100%;
  min-height: 70vh;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 600;
`;

const FormGroup = styled.div`
  margin-bottom: 10px;
`;

const Label = styled.div`
  margin-bottom: 4px;
  font-weight: 500;
  font-size: 13px;
`;

const StatusBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ActiveDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => props.active ? '#52c41a' : '#d9d9d9'};
  display: inline-block;
  animation: ${props => props.active ? 'pulse 2s infinite' : 'none'};

  @keyframes pulse {
    0% { box-shadow: 0 0 0 0 rgba(82, 196, 26, 0.4); }
    70% { box-shadow: 0 0 0 6px rgba(82, 196, 26, 0); }
    100% { box-shadow: 0 0 0 0 rgba(82, 196, 26, 0); }
  }
`;

const TitleCell = styled.div`
  font-weight: 500;
  color: #262626;
`;

const StyledTable = styled(Table)`
  .ant-table-row.row-inactive {
    background-color: #fafafa;

    td {
      color: #8c8c8c;
    }
  }

  .ant-table-row.row-active {
    background-color: #f6ffed;

    &:hover > td {
      background-color: #d9f7be !important;
    }
  }
`;

const StyledModal = styled(Modal)`
  .ant-modal-header {
    border-bottom: none;
    padding-bottom: 0;
  }

  .ant-modal-body {
    padding-top: 12px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  .icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
  }

  .icon-create {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
  }

  .icon-edit {
    background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
    color: white;
  }

  .title-text {
    font-size: 16px;
    font-weight: 600;
    color: #262626;
  }

  .subtitle {
    font-size: 12px;
    color: #8c8c8c;
  }
`;

const SectionTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #8c8c8c;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const OptionCard = styled.div`
  border: 2px solid ${props => props.selected ? props.color || '#1890ff' : '#f0f0f0'};
  background: ${props => props.selected ? `${props.color}10` : '#fafafa'};
  border-radius: 8px;
  padding: 8px 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;

  &:hover {
    border-color: ${props => props.color || '#1890ff'};
    background: ${props => `${props.color}08` || '#f0f8ff'};
  }

  .option-icon {
    font-size: 18px;
    color: ${props => props.selected ? props.color || '#1890ff' : '#8c8c8c'};
    margin-bottom: 2px;
  }

  .option-label {
    font-size: 11px;
    font-weight: 500;
    color: ${props => props.selected ? '#262626' : '#8c8c8c'};
  }
`;

const SettingsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #fafafa;
  border-radius: 8px;
  margin-bottom: 6px;

  .setting-info {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .setting-icon {
    color: #8c8c8c;
    font-size: 14px;
  }

  .setting-label {
    font-weight: 500;
    font-size: 13px;
    color: #262626;
  }

  .setting-desc {
    font-size: 11px;
    color: #8c8c8c;
  }
`;

// const TARGET_OPTIONS = [
//   { label: 'All Users', value: 'all' },
//   { label: 'Authenticated Users', value: 'all_auth' },
//   { label: 'Providers', value: 'provider' },
//   { label: 'Participants', value: 'participant' },
// ];
//
// const ANNOUNCEMENT_TYPE_OPTIONS = [
//   { label: 'Banner', value: 'banner' },
//   { label: 'Notification', value: 'notification' },
//   { label: 'Modal', value: 'modal' },
// ];
//
// const SEVERITY_OPTIONS = [
//   { label: 'Info', value: 'info' },
//   { label: 'Warning', value: 'warning' },
//   { label: 'Error', value: 'error' },
//   { label: 'Success', value: 'success' },
// ];

const DEFAULT_FORM = {
  title: '',
  data: '',
  target: 'all_auth',
  announcementType: 'modal',
  severity: 'info',
  isPersistent: false,
  duration: 5,
  deactivateAt: null,
};

const AnnouncementPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUuid, setEditingUuid] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_FORM);

  const { data = [], isLoading } = useGetApi(ProviderAPI.GET_ANNOUNCEMENTS_ADMIN);

  const createMutation = useMutateApi(ProviderAPI.POST_ANNOUNCEMENT, 'POST', {
    invalidation: [{ endpoint: ProviderAPI.GET_ANNOUNCEMENTS_ADMIN, params: {} }],
    onSuccess: () => {
      toast.success('Announcement created successfully');
      handleCloseModal();
    },
    onError: (error) => {
      toast.error(error?.body?.message || 'Failed to create announcement');
    },
  });

  const updateMutation = useMutateApi(ProviderAPI.PATCH_ANNOUNCEMENT, 'PATCH', {
    params: { uuid: editingUuid },
    invalidation: [{ endpoint: ProviderAPI.GET_ANNOUNCEMENTS_ADMIN, params: {} }],
    onSuccess: () => {
      toast.success('Announcement updated successfully');
      handleCloseModal();
    },
    onError: (error) => {
      toast.error(error?.body?.message || 'Failed to update announcement');
    },
  });

  const handleOpenCreate = () => {
    setEditingUuid(null);
    setFormData(DEFAULT_FORM);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (announcement) => {
    setEditingUuid(announcement.uuid);
    setFormData({
      title: announcement.title || '',
      data: announcement.data || '',
      target: announcement.target || 'all_auth',
      announcementType: announcement.announcementType || 'banner',
      severity: announcement.severity || 'info',
      isPersistent: announcement.isPersistent || false,
      duration: announcement.duration || 5,
      deactivateAt: announcement.deactivateAt || null,
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingUuid(null);
    setFormData(DEFAULT_FORM);
  };

  const handleDeactivate = () => {
    if (!editingUuid) return;
    updateMutation.mutate({ deactivateAt: new Date().toISOString() });
  };

  const isEditingAnnouncementActive = () => {
    if (!formData.deactivateAt) return true;
    return new Date(formData.deactivateAt) > new Date();
  };

  const handleSubmit = () => {
    if (!formData.title || !formData.data || !formData.announcementType) {
      toast.error('Please fill in required fields');
      return;
    }

    const submitData = {
      ...formData,
      isPersistent: formData.announcementType === 'banner' ? true : formData.isPersistent,
    };

    if (editingUuid) {
      updateMutation.mutate(submitData);
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const isActive = (record) => {
    if (!record.deactivateAt) return true;
    return new Date(record.deactivateAt) > new Date();
  };

  const getSeverityConfig = (severity) => {
    switch (severity) {
    case 'success': return { color: 'success', icon: <CheckCircleOutlined /> };
    case 'warning': return { color: 'warning', icon: <WarningOutlined /> };
    case 'error': return { color: 'error', icon: <ExclamationCircleOutlined /> };
    default: return { color: 'processing', icon: <InfoCircleOutlined /> };
    }
  };

  const getTypeConfig = (type) => {
    switch (type) {
    case 'banner': return { color: 'purple', icon: <AlertOutlined /> };
    case 'notification': return { color: 'blue', icon: <BellOutlined /> };
    case 'modal': return { color: 'cyan', icon: <DesktopOutlined /> };
    default: return { color: 'default', icon: null };
    }
  };

  const getTargetConfig = (target) => {
    switch (target) {
    case 'all': return { color: 'green', icon: <GlobalOutlined />, label: 'All Users' };
    case 'all_auth': return { color: 'blue', icon: <TeamOutlined />, label: 'Authenticated' };
    case 'provider': return { color: 'orange', icon: <UserOutlined />, label: 'Providers' };
    case 'participant': return { color: 'magenta', icon: <UserOutlined />, label: 'Participants' };
    default: return { color: 'default', icon: null, label: target };
    }
  };

  const columns = [
    {
      title: 'Status',
      key: 'status',
      width: 80,
      align: 'center',
      render: (_, record) => {
        const active = isActive(record);
        return (
          <Tooltip title={active ? 'Active' : 'Inactive'}>
            <StatusBadge>
              <ActiveDot active={active} />
              <span style={{ fontSize: 12, color: active ? '#52c41a' : '#8c8c8c' }}>
                {active ? 'Live' : 'Ended'}
              </span>
            </StatusBadge>
          </Tooltip>
        );
      },
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 180,
      render: (value, record) => (
        <TitleCell style={{ opacity: isActive(record) ? 1 : 0.5 }}>
          {value || <span style={{ color: '#bfbfbf', fontStyle: 'italic' }}>No title</span>}
        </TitleCell>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'announcementType',
      key: 'announcementType',
      width: 130,
      render: (value) => {
        const config = getTypeConfig(value);
        return (
          <Tag icon={config.icon} color={config.color} style={{ textTransform: 'capitalize' }}>
            {value}
          </Tag>
        );
      },
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      width: 120,
      render: (value) => {
        const config = getSeverityConfig(value);
        return (
          <Tag icon={config.icon} color={config.color} style={{ textTransform: 'capitalize' }}>
            {value}
          </Tag>
        );
      },
    },
    {
      title: 'Target',
      dataIndex: 'target',
      key: 'target',
      width: 140,
      render: (value) => {
        const config = getTargetConfig(value);
        return (
          <Tag icon={config.icon} color={config.color}>
            {config.label}
          </Tag>
        );
      },
    },
    {
      title: 'Options',
      key: 'options',
      width: 140,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          {record.announcementType === 'notification' && (
            <Tooltip title="Auto-dismiss duration">
              <Tag icon={<ClockCircleOutlined />} color="default">{record.duration}s</Tag>
            </Tooltip>
          )}
          {record.isPersistent ? (
            <Tag icon={<PushpinOutlined />} color="gold">Persistent</Tag>
          ) : (
            <Tag color="default">One-time</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Live Till',
      dataIndex: 'deactivateAt',
      key: 'deactivateAt',
      width: 140,
      render: (value, record) => {
        if (!value) {
          return <Tag color="green">Forever</Tag>;
        }
        const active = isActive(record);
        return (
          <span style={{ color: active ? '#fa8c16' : '#8c8c8c', fontSize: 13 }}>
            {moment(value).format('MMM D, YYYY HH:mm')}
          </span>
        );
      },
    },
  ];

  return (
    <Page title="Announcements">
      <SiteAdmin>
        <Container>
          <Header>
            <Title>Announcements</Title>
            <Button type="primary" onClick={handleOpenCreate}>
              Create Announcement
            </Button>
          </Header>

          <StyledTable
            loading={isLoading}
            dataSource={data}
            columns={columns}
            rowKey="uuid"
            pagination={{ pageSize: 10 }}
            rowClassName={(record) => isActive(record) ? 'row-active' : 'row-inactive'}
            onRow={(record) => ({
              onClick: () => handleOpenEdit(record),
              style: { cursor: 'pointer' },
            })}
          />

          <StyledModal
            centered
            title={
              <ModalHeader>
                <div className={`icon ${editingUuid ? 'icon-edit' : 'icon-create'}`}>
                  {editingUuid ? <EditOutlined /> : <PlusOutlined />}
                </div>
                <div>
                  <div className="title-text">{editingUuid ? 'Edit Announcement' : 'Create Announcement'}</div>
                  <div className="subtitle">{editingUuid ? 'Update the announcement details' : 'Create a new announcement for users'}</div>
                </div>
              </ModalHeader>
            }
            visible={isModalOpen}
            onCancel={handleCloseModal}
            onOk={handleSubmit}
            width={600}
            footer={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  {editingUuid && isEditingAnnouncementActive() && (
                    <Button
                      danger
                      icon={<StopOutlined />}
                      onClick={handleDeactivate}
                      loading={updateMutation.isLoading}
                    >
                      Deactivate Now
                    </Button>
                  )}
                </div>
                <Space>
                  <Button onClick={handleCloseModal}>Cancel</Button>
                  <Button
                    type="primary"
                    onClick={handleSubmit}
                    loading={createMutation.isLoading || updateMutation.isLoading}
                    style={{
                      background: editingUuid
                        ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: 'none',
                    }}
                  >
                    {editingUuid ? 'Update' : 'Create'}
                  </Button>
                </Space>
              </div>
            }
          >
            <Divider style={{ margin: '16px 0' }} />

            <SectionTitle>Content</SectionTitle>
            <FormGroup>
              <Label>Title *</Label>
              <Input
                placeholder="Enter announcement title"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
              />
              {formData.announcementType === 'banner' && (
                <div style={{ fontSize: 12, color: '#faad14', marginTop: 4 }}>
                  <WarningOutlined style={{ marginRight: 4 }} />
                  Title won&apos;t be displayed for banner announcements
                </div>
              )}
            </FormGroup>

            <FormGroup>
              <Label>Message *</Label>
              <div style={{border: '1px solid #d9d9d9', borderRadius: 8, padding: 10, minHeight: 120}}>
                <RichTextField
                  inputValue={formData.data}
                  allowEdit={true}
                  onChange={(value) => handleChange('data', value)}
                  placeholder="Enter announcement message..."
                  numberedList={false}
                  bulletList={false}
                  counter={false}
                />
              </div>
            </FormGroup>

            <Divider style={{ margin: '12px 0' }} />

            <SectionTitle>Announcement Type</SectionTitle>
            <Row gutter={8} style={{ marginBottom: 12 }}>
              <Col span={8}>
                <OptionCard
                  selected={formData.announcementType === 'modal'}
                  color="#13c2c2"
                  onClick={() => handleChange('announcementType', 'modal')}
                >
                  <div className="option-icon"><DesktopOutlined /></div>
                  <div className="option-label">Modal</div>
                </OptionCard>
              </Col>
              <Col span={8}>
                <OptionCard
                  selected={formData.announcementType === 'notification'}
                  color="#1890ff"
                  onClick={() => handleChange('announcementType', 'notification')}
                >
                  <div className="option-icon"><BellOutlined /></div>
                  <div className="option-label">Notification</div>
                </OptionCard>
              </Col>
              <Col span={8}>
                <OptionCard
                  selected={formData.announcementType === 'banner'}
                  color="#722ed1"
                  onClick={() => handleChange('announcementType', 'banner')}
                >
                  <div className="option-icon"><AlertOutlined /></div>
                  <div className="option-label">Banner</div>
                </OptionCard>
              </Col>
            </Row>

            <SectionTitle>Severity</SectionTitle>
            <Row gutter={8} style={{ marginBottom: 12 }}>
              <Col span={6}>
                <OptionCard
                  selected={formData.severity === 'info'}
                  color="#1890ff"
                  onClick={() => handleChange('severity', 'info')}
                >
                  <div className="option-icon"><InfoCircleOutlined /></div>
                  <div className="option-label">Info</div>
                </OptionCard>
              </Col>
              <Col span={6}>
                <OptionCard
                  selected={formData.severity === 'success'}
                  color="#52c41a"
                  onClick={() => handleChange('severity', 'success')}
                >
                  <div className="option-icon"><CheckCircleOutlined /></div>
                  <div className="option-label">Success</div>
                </OptionCard>
              </Col>
              <Col span={6}>
                <OptionCard
                  selected={formData.severity === 'warning'}
                  color="#faad14"
                  onClick={() => handleChange('severity', 'warning')}
                >
                  <div className="option-icon"><WarningOutlined /></div>
                  <div className="option-label">Warning</div>
                </OptionCard>
              </Col>
              <Col span={6}>
                <OptionCard
                  selected={formData.severity === 'error'}
                  color="#ff4d4f"
                  onClick={() => handleChange('severity', 'error')}
                >
                  <div className="option-icon"><ExclamationCircleOutlined /></div>
                  <div className="option-label">Error</div>
                </OptionCard>
              </Col>
            </Row>

            <SectionTitle>Target Audience</SectionTitle>
            <Row gutter={8} style={{ marginBottom: 12 }}>
              <Col span={6}>
                <OptionCard
                  selected={formData.target === 'all'}
                  color="#52c41a"
                  onClick={() => handleChange('target', 'all')}
                >
                  <div className="option-icon"><GlobalOutlined /></div>
                  <div className="option-label">All Users</div>
                </OptionCard>
              </Col>
              <Col span={6}>
                <OptionCard
                  selected={formData.target === 'all_auth'}
                  color="#1890ff"
                  onClick={() => handleChange('target', 'all_auth')}
                >
                  <div className="option-icon"><TeamOutlined /></div>
                  <div className="option-label">Authenticated</div>
                </OptionCard>
              </Col>
              <Col span={6}>
                <OptionCard
                  selected={formData.target === 'provider'}
                  color="#fa8c16"
                  onClick={() => handleChange('target', 'provider')}
                >
                  <div className="option-icon"><UserOutlined /></div>
                  <div className="option-label">Providers</div>
                </OptionCard>
              </Col>
              <Col span={6}>
                <OptionCard
                  selected={formData.target === 'participant'}
                  color="#eb2f96"
                  onClick={() => handleChange('target', 'participant')}
                >
                  <div className="option-icon"><UserOutlined /></div>
                  <div className="option-label">Participants</div>
                </OptionCard>
              </Col>
            </Row>

            <Divider style={{ margin: '12px 0' }} />

            <SectionTitle style={{ opacity: formData.announcementType === 'banner' ? 0.5 : 1 }}>
              Settings {formData.announcementType === 'banner' && <span style={{ fontSize: 10, fontWeight: 400 }}>(Not applicable for banners)</span>}
            </SectionTitle>
            <SettingsRow style={{ opacity: formData.announcementType === 'banner' ? 0.5 : 1 }}>
              <div className="setting-info">
                <PushpinOutlined className="setting-icon" />
                <div>
                  <div className="setting-label">Persistent</div>
                  <div className="setting-desc">Keep showing in every session</div>
                </div>
              </div>
              <Switch
                checked={formData.announcementType === 'banner' ? true : formData.isPersistent}
                onChange={(checked) => handleChange('isPersistent', checked)}
                disabled={formData.announcementType === 'banner'}
              />
            </SettingsRow>

            <SettingsRow style={{ opacity: formData.announcementType !== 'notification' ? 0.5 : 1 }}>
              <div className="setting-info">
                <ClockCircleOutlined className="setting-icon" />
                <div>
                  <div className="setting-label">Auto-dismiss Duration</div>
                  <div className="setting-desc">Seconds before auto-closing (notifications only)</div>
                </div>
              </div>
              <Input
                type="number"
                min={1}
                value={formData.duration}
                onChange={(e) => handleChange('duration', Number(e.target.value))}
                style={{ width: 80 }}
                disabled={formData.announcementType !== 'notification'}
              />
            </SettingsRow>

            <FormGroup style={{ marginTop: 10 }}>
              <Label>Deactivate At</Label>
              <DatePicker
                showTime
                style={{ width: '100%' }}
                placeholder="Leave empty to keep active forever"
                value={formData.deactivateAt ? moment(formData.deactivateAt) : null}
                onChange={(date) => handleChange('deactivateAt', date ? date.toISOString() : null)}
              />
            </FormGroup>
          </StyledModal>
        </Container>
      </SiteAdmin>
    </Page>
  );
};

export default withAdminOnly(AnnouncementPage);
